import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '../state/auth.store';
import { RightKey, Rights } from '../../constants/rights.constants';
import { isOnboardingComplete } from '../../constants/onboarding.constants';
import { firstValueFrom, map, of, switchMap } from 'rxjs';
import { defaultAuthenticatedRoute, isShellOnlyUser } from '../utils/auth-routing.util';
import { restoreAuthSession } from '../utils/auth-session.util';

export const hasRight = (right: RightKey): CanActivateFn => {
  return (route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    const checkRight = (): boolean => {
      if (!authStore.isAuthenticated()) {
        router.navigate(['/login']);
        return false;
      }

      if (authStore.isSuperAdmin() || authStore.hasRight()(right)) {
        return true;
      }

      router.navigate([defaultAuthenticatedRoute(authStore.user())]);
      return false;
    };

    if (authStore.isAuthenticated()) {
      return checkRight();
    }

    return restoreAuthSession(authStore).pipe(
      map(() => checkRight())
    );
  };
};

export const authGuard: CanActivateFn = (route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  // Helper to check onboarding completion
  const checkOnboarding = (): boolean => {
    const user = authStore.user();
    const isShellRoute = state.url.startsWith('/jivu-shell') || state.url.startsWith('/jivu-overlay');
    const canUseShell = !!user && authStore.hasRight()(Rights.JivuTalkShell_Access);
    const shellOnly = isShellOnlyUser(user);
    if (shellOnly && !isShellRoute) {
      router.navigate(['/jivu-shell']);
      return false;
    }

    // Allow access to onboarding page itself
    if (state.url.startsWith('/onboarding')) return true;
    if (isShellRoute && canUseShell) return true;
    // Redirect to onboarding for users who have not reached the completed step.
    if (user && !isOnboardingComplete(user.onboardingStep)) {
      if (shellOnly) {
        router.navigate(['/jivu-shell']);
        return false;
      }

      const code = route.queryParams['code'];
      const oauthState = route.queryParams['state'];
      if (code && oauthState) {
        router.navigate(['/onboarding'], { queryParams: { code, state: oauthState } });
      } else {
        router.navigate(['/onboarding']);
      }
      return false;
    }
    return true;
  };

  if (authStore.isAuthenticated()) {
    return checkOnboarding();
  }

  return restoreAuthSession(authStore).pipe(
    switchMap(() => {
      if (!authStore.isAuthenticated()) {
        router.navigate(['/login']);
        return of(false);
      }

      return of(checkOnboarding());
    })
  );
};

export const jivuOverlayGuard: CanActivateFn = async () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const token = await resolveNativeOverlayGuardToken();
  if (token) {
    authStore.hydrateOverlaySession(token);
  }

  const refreshNativeOverlayUser = async (): Promise<void> => {
    if (token && authStore.isAuthenticated() && !authStore.user()) {
      await firstValueFrom(authStore.refreshUserFromApi());
    }
  };

  const checkAccess = (): boolean => {
    if (!authStore.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }

    if (authStore.isSuperAdmin() || authStore.hasRight()(Rights.JivuTalkShell_Access)) {
      return true;
    }

    router.navigate([defaultAuthenticatedRoute(authStore.user())]);
    return false;
  };

  if (authStore.isAuthenticated()) {
    await refreshNativeOverlayUser();
    return checkAccess();
  }

  await firstValueFrom(restoreAuthSession(authStore));
  await refreshNativeOverlayUser();
  return checkAccess();
};

async function resolveNativeOverlayGuardToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  const globalToken = (window as any).__HEYJIVU_OVERLAY_TOKEN__;
  if (typeof globalToken === 'string' && globalToken.trim()) {
    return globalToken.trim();
  }

  try {
    return await (window.heyjivuDesktop?.overlay?.getSessionToken?.() ?? Promise.resolve(null));
  } catch {
    return null;
  }
}

export const guestGuard: CanActivateFn = (route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (authStore.isAuthenticated()) {
    router.navigate([defaultAuthenticatedRoute(authStore.user())]);
    return false;
  }

  return restoreAuthSession(authStore).pipe(
    map(() => {
      if (authStore.isAuthenticated()) {
        router.navigate([defaultAuthenticatedRoute(authStore.user())]);
        return false;
      }

      return true;
    })
  );
};
